import { useEffect } from 'react';

import type {
  ExternalRouteRef,
  RouteRef,
  SubRouteRef,
} from '@backstage/core-plugin-api';
import { useRouteRef } from '@backstage/core-plugin-api';

import {
  RECENT_ACTIVITY_TYPENAMES,
  registerRecentActivityResources,
} from '@spotify/confidence-core-react';
import {
  assignmentTablesRouteRef,
  collectionsRouteRef,
  entitiesRouteRef,
  factTablesRouteRef,
} from '@spotify/plugin-confidence-admin';
import { chatsRouteRef } from '@spotify/plugin-confidence-chat';
import { rootRouteRef as eventsRouteRef } from '@spotify/plugin-confidence-events';
import { flagsRouteRef } from '@spotify/plugin-confidence-flags';
import { rootRouteRef as materializedSegmentsRouteRef } from '@spotify/plugin-confidence-flags-module-materialized-segments';
import { rootRouteRef as segmentsRouteRef } from '@spotify/plugin-confidence-flags-module-segments';
import { rootRouteRef as initiativesRouteRef } from '@spotify/plugin-confidence-initiatives';
import { pulsesRouteRef } from '@spotify/plugin-confidence-insights';
import { issuesRouteRef } from '@spotify/plugin-confidence-issues';
import { measurementsRouteRef } from '@spotify/plugin-confidence-metrics';
import { metricsRouteRef } from '@spotify/plugin-confidence-metrics-react';
import { rootRouteRef as projectsRouteRef } from '@spotify/plugin-confidence-projects';
import { explorerRouteRef as recordingsRouteRef } from '@spotify/plugin-confidence-recordings';
import { surfacesRouteRef } from '@spotify/plugin-confidence-surfaces';

type AnyRouteRef =
  | RouteRef<undefined>
  | ExternalRouteRef<undefined, boolean>
  | SubRouteRef<undefined>;

const useSafeRouteRef = (ref: AnyRouteRef): string | undefined => {
  try {
    const resolve = useRouteRef(ref as ExternalRouteRef<undefined, true>);
    return resolve?.();
  } catch {
    return undefined;
  }
};

export const RecentActivityResourceSetup = () => {
  const flags = useSafeRouteRef(flagsRouteRef);
  const materializedSegments = useSafeRouteRef(materializedSegmentsRouteRef);
  const segments = useSafeRouteRef(segmentsRouteRef);
  const metrics = useSafeRouteRef(metricsRouteRef);
  const measurements = useSafeRouteRef(measurementsRouteRef);
  const events = useSafeRouteRef(eventsRouteRef);
  const surfaces = useSafeRouteRef(surfacesRouteRef);
  const issues = useSafeRouteRef(issuesRouteRef);
  const projects = useSafeRouteRef(projectsRouteRef);
  const chats = useSafeRouteRef(chatsRouteRef);
  const initiatives = useSafeRouteRef(initiativesRouteRef);
  const recordings = useSafeRouteRef(recordingsRouteRef);
  const pulses = useSafeRouteRef(pulsesRouteRef);
  const collections = useSafeRouteRef(collectionsRouteRef);
  const assignmentTables = useSafeRouteRef(assignmentTablesRouteRef);
  const factTables = useSafeRouteRef(factTablesRouteRef);
  const entities = useSafeRouteRef(entitiesRouteRef);

  useEffect(() => {
    const resources: Record<string, { route: string; typename?: string }> = {};

    if (flags)
      resources.flags = {
        route: flags,
        typename: RECENT_ACTIVITY_TYPENAMES.flags,
      };
    if (segments)
      resources.segments = {
        route: segments,
        typename: RECENT_ACTIVITY_TYPENAMES.segments,
      };
    if (materializedSegments)
      resources.materializedSegments = {
        route: materializedSegments,
        typename: RECENT_ACTIVITY_TYPENAMES.materializedSegments,
      };
    if (metrics)
      resources.metrics = {
        route: metrics,
        typename: RECENT_ACTIVITY_TYPENAMES.metrics,
      };
    if (measurements)
      resources.measurements = {
        route: measurements,
        typename: RECENT_ACTIVITY_TYPENAMES.measurements,
      };
    if (events)
      resources.eventDefinitions = {
        route: events,
        typename: RECENT_ACTIVITY_TYPENAMES.eventDefinitions,
      };
    if (surfaces)
      resources.surfaces = {
        route: surfaces,
        typename: RECENT_ACTIVITY_TYPENAMES.surfaces,
      };
    if (issues)
      resources.issues = {
        route: issues,
        typename: RECENT_ACTIVITY_TYPENAMES.issues,
      };
    if (projects)
      resources.projects = {
        route: projects,
        typename: RECENT_ACTIVITY_TYPENAMES.projects,
      };
    if (chats)
      resources.chats = {
        route: chats,
        typename: RECENT_ACTIVITY_TYPENAMES.chats,
      };
    if (initiatives)
      resources.initiatives = {
        route: initiatives,
        typename: RECENT_ACTIVITY_TYPENAMES.initiatives,
      };
    if (recordings) resources.recordings = { route: recordings };
    if (pulses)
      resources.pulses = {
        route: pulses,
        typename: RECENT_ACTIVITY_TYPENAMES.pulses,
      };
    if (collections)
      resources.collections = {
        route: collections,
        typename: RECENT_ACTIVITY_TYPENAMES.collections,
      };
    if (assignmentTables)
      resources.assignmentTables = {
        route: assignmentTables,
        typename: RECENT_ACTIVITY_TYPENAMES.assignmentTables,
      };
    if (factTables)
      resources.factTables = {
        route: factTables,
        typename: RECENT_ACTIVITY_TYPENAMES.factTables,
      };
    if (entities)
      resources.entities = {
        route: entities,
        typename: RECENT_ACTIVITY_TYPENAMES.entities,
      };

    registerRecentActivityResources(resources);
  }, [
    flags,
    materializedSegments,
    segments,
    metrics,
    measurements,
    events,
    surfaces,
    issues,
    projects,
    chats,
    initiatives,
    recordings,
    pulses,
    collections,
    assignmentTables,
    factTables,
    entities,
  ]);

  return null;
};
